import React, { Suspense, useEffect, useCallback, useMemo } from 'react';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import pluralize from 'pluralize';
import _ from 'lodash';

import Spinner from '../../Spinner/Spinner';
import { getListState, saveListState } from '../../../lib/ListStateHelper';

const TableDocumentSetup = React.lazy(() => import('../../Table/TableDocumentSetup/TableDocumentSetup'));
const FormInput = React.lazy(() => import('../../FormInput/FormInput'));
const ConfirmationModal = React.lazy(() => import('../../Modals/ConfirmationModal/ConfirmationModal'));

export default function DocumentSetupList(props) {
    const listKey = props.listKey || window.location.pathname;
    const savedState = useMemo(() => getListState(listKey) || {}, [listKey]);

    const [activeFilter, setActiveFilter] = React.useState(savedState.activeFilter !== false);
    const [filteredList, setFilteredList] = React.useState([]);
    const [searchValue, setSearchValue] = React.useState(savedState.search || '');
    const [page, setPage] = React.useState(savedState.page || 1);
    const [sizePerPage, setSizePerPage] = React.useState(savedState.sizePerPage || props.sizePerPage || 50);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState();

    const persistState = useCallback((updates) => {
        saveListState(listKey, updates);
    }, [listKey]);

    const filterList = useCallback((listData) => {
        let filtered = listData;
        if (!_.isEmpty(listData) && !_.isEmpty(props.columns)) {
            const filteredData = listData.filter((item) => {
                return props.activeValue === item[props.activeField]
            });
            filtered = filteredData;
        }

        return filtered;
    }, [props.activeField, props.activeValue, props.columns]);

    const searchList = (listData, searchValue) => {
        let filtered = listData;
        if (!_.isEmpty(listData)) {
            filtered = listData.filter((data) => {
                let valid = false;
                let item = null;
                for (let c = 0; c < props.columns.length; c++) {
                    const dataField = props.columns[c].dataField;
                    item = data[dataField];

                    if (dataField.includes('.')) {
                        var fields = dataField.split('.');
                        item = data[fields[0]];
                        if (fields.length > 1) {
                            for (let f = 1; f < fields.length; f++) {
                                item = item[fields[f]];
                            }
                        }
                    }
                    if (typeof item === 'string') {
                        valid = item.toLowerCase().includes(searchValue.toLowerCase());
                        if (valid) {
                            break;
                        };
                    }
                }

                return (valid)
                    ? item
                    : null;
            });
        }

        return filtered;
    };

    const applyFilters = useCallback((listData, search, activeOnly) => {
        let filtered = listData || [];

        if (!props.hideFilter && activeOnly === true) {
            filtered = filterList(filtered);
        }

        if (!_.isEmpty(search)) {
            filtered = searchList(filtered, search.toLowerCase());
        }

        return filtered;
    }, [props.hideFilter, filterList, props.columns]);

    useEffect(() => {
        setFilteredList(applyFilters(props.listData, searchValue, activeFilter));
    }, [props.listData, searchValue, activeFilter, applyFilters]);

    const refreshHandler = () => {
        if (props.refreshed) {
            props.refreshed();
        }
    };

    const addHandler = () => {
        if (props.added) {
            props.added();
        }
    };

    const copyHandler = (selectedItem) => {
        if (props.copied) { props.copied(selectedItem); }
    };

    const viewDocumentMapperHandler = (selectedItem) => {
        if (props.viewDocumentMapper) {
            props.viewDocumentMapper(selectedItem);
        }
    };

    const activatePageHandler = (selectedItem) => {
        if (props.activatePage) {
            props.activatePage(selectedItem);
        }
    };

    const showPageSourceHandler = (selectedItem) => {
        if (props.showPageSource) {
            props.showPageSource(selectedItem);
        }
    }

    const viewDocumentPagesHandler = (selectedItem) => {
        if (props.viewDocumentPages) {
            props.viewDocumentPages(selectedItem);
        }
    };

    const viewMembersPageHandler = (selectedItem) => {
        if (props.viewMembersPage) {
            props.viewMembersPage(selectedItem);
        }
    };

    const viewDocumentDetailHandler = (selectedItem) => {
        if (props.viewDocumentDetail) {
            props.viewDocumentDetail(selectedItem);
        }
    };

    const viewDocumentSourceHandler = (selectedItem) => {
        if (props.viewDocumentSource) {
            props.viewDocumentSource(selectedItem);
        }
    };

    const selectHandler = (selectedItem) => {
        if (props.selected) {
            props.selected(selectedItem);
        }
    };

    const deleteHandler = (selectedItem) => {
        setShowDeleteModal(true);
        setSelectedItem(selectedItem);
    };

    const noDeleteHandler = () => {
        setShowDeleteModal(false);
    };

    const yesDeleteHandler = () => {
        setShowDeleteModal(false);

        if (props.deleted) { props.deleted(selectedItem); }
    };

    const searchHandler = (event) => {
        const value = event.target.value;
        setSearchValue(value);
        setPage(1);
        persistState({ search: value, page: 1 });
    };

    const resetHandler = () => {
        setSearchValue('');
        setPage(1);
        persistState({ search: '', page: 1 });
    };

    const filterHandler = (_id, value) => {
        setActiveFilter(value);
        setPage(1);
        persistState({ activeFilter: value, page: 1 });

        if (props.filtered) { props.filtered(value); }
    };

    const pageChangedHandler = (nextPage, nextPageSize) => {
        // avoid repeating updates if values are unchanged (prevents nested update loops)
        if (nextPage !== page) { setPage(nextPage); }
        if (nextPageSize !== sizePerPage) { setSizePerPage(nextPageSize); }
        if (nextPage !== page || nextPageSize !== sizePerPage) {
            persistState({ page: nextPage, sizePerPage: nextPageSize });
        }

        if (_.isFunction(props.pageChanged)) {
            props.pageChanged(nextPage, nextPageSize);
        }
    };

    const pageSizeChangedHandler = (nextPage, nextPageSize) => {
        // avoid repeating updates if values are unchanged (prevents nested update loops)
        if (nextPage !== page) { setPage(nextPage); }
        if (nextPageSize !== sizePerPage) { setSizePerPage(nextPageSize); }
        if (nextPage !== page || nextPageSize !== sizePerPage) {
            persistState({ page: nextPage, sizePerPage: nextPageSize });
        }

        if (_.isFunction(props.pageSizeChanged)) {
            props.pageSizeChanged(nextPage, nextPageSize);
        }
    };

    const plural = pluralize(props.compType, 0);
    const noDataText = `No ${plural} Available.`;
    const keyField = _.isEmpty(props.keyField)
        ? 'id'
        : props.keyField;

    let table = (
        <TableDocumentSetup keyField={keyField}
            responsive
            data={filteredList}
            sorting={props.sorting}
            columns={props.columns}
            isDisabled={props.isDisabled}
            hideEdit={props.hideEdit}
            hideDelete={!props.permissionEdit || props.hideDelete}
            hideCopy={!props.permissionEdit || props.hideCopy}
            hideAdd={!props.permissionEdit || props.hideAdd}
            hideRefresh={props.hideRefresh}
            hideSizePerPage={props.hideSizePerPage}
            hidePagination={props.hidePagination}
            hidePrint={props.hidePrint}
            hasCheckbox={props.hasCheckbox}
            hideDocumentMapper={!props.permissionEdit || props.hideDocumentMapper}
            hideActivatePage={!props.permissionEdit || props.hideActivatePage}
            hideViewPages={!props.permissionEdit || props.hideViewPages}
            hideMembersPage={props.hideMembersPage}
            hideViewDetail={props.hideViewDetail}
            hideViewSource={!props.permissionEdit || props.hideViewSource}
            showActions={props.showActions}
            permissionEdit={props.permissionEdit}
            page={page}
            sizePerPage={sizePerPage}
            noDataText={noDataText}
            pageChanged={(nextPage, nextPageSize) => pageChangedHandler(nextPage, nextPageSize)}
            pageSizeChanged={(nextPage, nextPageSize) => pageSizeChangedHandler(nextPage, nextPageSize)}
            selected={(selectedItem) => selectHandler(selectedItem)}
            deleted={(selectedItem) => deleteHandler(selectedItem)}
            copied={(selectedItem) => copyHandler(selectedItem)}
            activatePage={(selectedItem) => activatePageHandler(selectedItem)}
            showPageSource={(selectedItem) => showPageSourceHandler(selectedItem)}
            viewDocumentMapper={(selectedItem) => viewDocumentMapperHandler(selectedItem)}
            viewDocumentPages={(selectedItem) => viewDocumentPagesHandler(selectedItem)}
            viewMembersPage={(selectedItem) => viewMembersPageHandler(selectedItem)}
            viewDocumentDetail={(selectedItem) => viewDocumentDetailHandler(selectedItem)}
            viewDocumentSource={(selectedItem) => viewDocumentSourceHandler(selectedItem)}
        />
    );

    let addButton = null;
    let tableActions = null;
    let deleteModal = null;

    if (props.isBusy) {
        table = <Spinner text={`Loading ${plural}...`} />;
    } else {
        const refreshButton = (!props.hideRefresh) && (
            <Button bsStyle="default" onClick={refreshHandler} className="button-group-margin">
                <i className="fa fa-refresh button-icon"></i>
                Refresh
            </Button>
        );

        if (props.permissionEdit && !props.hideAdd) {
            const buttonText = (props.addButtonText)
                ? props.addButtonText
                : `Add ${props.compType}`;

            addButton = (
                <Button bsStyle="danger" onClick={addHandler} className="button-group-margin">
                    <i className="fa fa-plus button-icon"></i>
                    {buttonText}
                </Button>
            );
        }

        const search = (!props.hideSearch) && (
            <InputGroup style={{ width: '300px' }}>
                <InputGroup.Addon>Search:</InputGroup.Addon>
                <FormControl id="search" type="text" maxLength={50}
                    value={searchValue}
                    autoComplete="off"
                    onChange={(event) => searchHandler(event)} />
                <InputGroup.Button>
                    <Button bsStyle="default" onClick={resetHandler}>Reset</Button>
                </InputGroup.Button>
            </InputGroup>
        );

        const filter = (!props.hideFilter) && (
            <div style={{ paddingLeft: '15px' }}>
                <FormInput type="checkbox"
                    id="activeFilter"
                    value={activeFilter}
                    labelText="Show Active Only"
                    changed={(id, value) => filterHandler(id, value)} />
            </div>
        );

        if (addButton || refreshButton || props.buttons) {
            tableActions = (
                <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '20px' }}>
                    <div style={{ width: '100%', minWidth: '30%', display: 'flex', alignItems: 'center' }}>
                        {search}
                        {props.moreFilters}
                        {filter}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '260px', minWidth: '260px' }}>
                        {refreshButton}
                        {props.buttons}
                        {addButton}
                    </div>
                </div>
            );
        }
    }

    if (showDeleteModal) {
        let deleteHeaderText = props.compType === 'Subscription'
            ? 'This ' + props.compType + ' will be removed from all Instances and all custom fees will be lost.'
            : 'If this ' + props.compType + ' is deleted, it and all related data will be unrecoverable.';

        deleteModal = (
            <ConfirmationModal
                show={showDeleteModal}
                noClicked={noDeleteHandler}
                yesClicked={yesDeleteHandler}>
                <p><strong>{deleteHeaderText}</strong></p>
                Are you sure you want to delete this {props.compType}?
            </ConfirmationModal>
        );
    }

    return (
        <Suspense fallback={<Spinner />}>
            {deleteModal}
            {tableActions}
            {table}
        </Suspense>
    );
}
